import { AgentTools, type ToolCallSummary } from '@/features/types/tools';
import { SoqlToolCall, type SoqlQueryToolCallSummary } from './tools/salesforce/SoqlToolCall';
import { DefaultToolCall } from './DefaultToolCall';
import { ToolCallErrorBoundary } from './ToolCallErrorBoundary';
import {
  ObjectDiscoverToolCall,
  type ObjectDiscoverToolCallSummary,
} from './tools/salesforce/ObjectDiscoverToolCall';
import { SoslToolCall, type SoslSearchToolCallSummary } from './tools/salesforce/SoslToolCall';
import { GetOrgLimit, type GetOrgLimitToolCallSummary } from './tools/salesforce/GetOrgLimit';
import { GetSObject, type GetSObjectToolCallSummary } from './tools/salesforce/GetSObject';
import { GetListViews, type GetListViewsToolCallSummary } from './tools/salesforce/GetListViews';
import {
  CreateRecordToolCall,
  type CreateRecordToolCallSummary,
} from './tools/salesforce/CreateRecordToolCall';
import {
  UpdateRecordToolCall,
  type UpdateRecordToolCallSummary,
} from './tools/salesforce/UpdateRecordToolCall';
import {
  DeleteRecordToolCall,
  type DeleteRecordToolCallSummary,
} from './tools/salesforce/DeleteRecordToolCall';
import {
  QueryAllToolCall,
  type QueryAllToolCallSummary,
} from './tools/salesforce/QueryAllToolCall';
import {
  SearchEngagementsToolCall,
  type SearchEngagementsToolCallSummary,
} from './tools/chorus/SearchEngagementsToolCall';
import {
  GetTranscriptToolCall,
  type GetTranscriptToolCallSummary,
} from './tools/chorus/GetTranscriptToolCall';
import {
  GetEngagementDetailsToolCall,
  type GetEngagementDetailsToolCallSummary,
} from './tools/chorus/GetEngagementDetailsToolCall';
import {
  SummarizeTranscriptToolCall,
  type SummarizeTranscriptToolCallSummary,
} from './tools/chorus/SummarizeTranscriptToolCall';
import {
  FieldDiscoverToolCall,
  type FieldDiscoverToolCallSummary,
} from './tools/salesforce/FieldDiscoverToolCall';

interface ToolCallRendererProps {
  toolCall: ToolCallSummary;
}

export function ToolCallRenderer({ toolCall }: ToolCallRendererProps) {
  const renderToolCall = () => {
    switch (toolCall.name) {
      // Salesforce tools
      case AgentTools.sf_soql_query:
        return <SoqlToolCall toolCall={toolCall as SoqlQueryToolCallSummary} />;

      case AgentTools.sf_sosl_search:
        return <SoslToolCall toolCall={toolCall as SoslSearchToolCallSummary} />;

      case AgentTools.sf_get_org_limits:
        return <GetOrgLimit toolCall={toolCall as GetOrgLimitToolCallSummary} />;

      case AgentTools.sf_get_sobject:
        return <GetSObject toolCall={toolCall as GetSObjectToolCallSummary} />;

      case AgentTools.sf_get_list_views:
        return <GetListViews toolCall={toolCall as GetListViewsToolCallSummary} />;

      case AgentTools.sf_create_record:
        return <CreateRecordToolCall toolCall={toolCall as CreateRecordToolCallSummary} />;

      case AgentTools.sf_update_record:
        return <UpdateRecordToolCall toolCall={toolCall as UpdateRecordToolCallSummary} />;

      case AgentTools.sf_delete_record:
        return <DeleteRecordToolCall toolCall={toolCall as DeleteRecordToolCallSummary} />;

      case AgentTools.sf_query_all:
        return <QueryAllToolCall toolCall={toolCall as QueryAllToolCallSummary} />;

      case AgentTools.sf_object_discover:
        return <ObjectDiscoverToolCall toolCall={toolCall as ObjectDiscoverToolCallSummary} />;

      case AgentTools.sf_field_discover:
        return <FieldDiscoverToolCall toolCall={toolCall as FieldDiscoverToolCallSummary} />;

      // Chorus tools
      case AgentTools.chorus_search_engagements:
        return (
          <SearchEngagementsToolCall toolCall={toolCall as SearchEngagementsToolCallSummary} />
        );

      case AgentTools.chorus_get_engagement_details:
        return (
          <GetEngagementDetailsToolCall
            toolCall={toolCall as GetEngagementDetailsToolCallSummary}
          />
        );

      case AgentTools.chorus_get_transcript:
        return <GetTranscriptToolCall toolCall={toolCall as GetTranscriptToolCallSummary} />;

      case AgentTools.chorus_summarize_transcript:
        return (
          <SummarizeTranscriptToolCall toolCall={toolCall as SummarizeTranscriptToolCallSummary} />
        );

      // Default
      default:
        return <DefaultToolCall toolCall={toolCall} />;
    }
  };

  return <ToolCallErrorBoundary toolCall={toolCall}>{renderToolCall()}</ToolCallErrorBoundary>;
}
